/* eslint-disable camelcase */
import React, {
	useCallback,
	useEffect,
	useState,
	useRef,
	useMemo,
} from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/styles';
import { ArrowClockwise } from 'react-bootstrap-icons';
import { TABLE_SETTING } from '../../constants/Tables';
import { useSessionState } from '../../hooks/useSessionState';
import usePersistentTableColumns from '../../hooks/usePersistentTableColumns';
import ColumnCustomizer from '../../components/OrdersTable/components/ColumnCustomizer';
import OrderFilters from '../../components/OrdersTable/components/OrderFilters';
import OrdersTable from '../../components/OrdersTable';
import ProductsTable from '../../components/ProductsTable';
import Error from '../../components/Error';
import Success from '../../components/Success';
import { SORTKEYS } from '../../constants/Orders';
import ordersColumnConfig, {
	columnOrder as defaultColumnOrder,
	columnVisibility as defaultColumnVisibility,
} from '../../components/OrdersTable/columnConfig';
import { makeProductsColumnConfig } from '../../components/ProductsTable/columnConfig';
import SideBarMenu from '../../components/SidebarMenu';
import ProductsFilters from '../../components/ProductsTable/components/ProductFilters';
import DataExport from '../../components/OrdersTable/components/DataExport';
import ClearButton from '../../components/ClearButton';
import { useSearchApi } from '../../apis';
import { useProductsApi } from '../../apis';
import { parseApiResponseData } from '../../apis/Utilities/parseApiResponseData';
import { downloadFileFromApi } from '../../apis/Utilities/downloadFileFromApi';

function Search(props) {
	const { pathname } = useLocation();
	const { searchKey } = useParams();
	const path = pathname.split('/').pop();
	const [limit, setLimit] = useSessionState(
		'search_orders_limit',
		TABLE_SETTING.DEFAULT_PAGE_SIZE
	);
	const [page, setPage] = useState(0);
	const theme = useTheme();
	const [sorts, setSorts] = useSessionState('orders_sorts', []);
	const openFiltersRef = useRef(null);
	const setPageParams = ({ limit: _limit, page: _page }) => {
		setLimit(_limit);
		setPage(_page);
	};
	const [ordersFilters, setOrdersFilters] = useState({});
	const [productsFilters, setProductsFilters] = useState({});
	const {
		columnOrder,
		setColumnOrder,
		columnVisibility,
		setColumnVisibility,
		resetColumns,
	} = usePersistentTableColumns({
		storageKeyPrefix: 'orders',
		defaultColumnOrder,
		defaultColumnVisibility,
	});

	const [isLoading, setIsLoading] = useState(true);

	const [orders, setOrders] = useState([]);
	const [ordersCount, setOrdersCount] = useState(0);

	const [products, setProducts] = useState([]);
	const [productsCount, setProductsCount] = useState(0);
	const [printCardDownloadError, setPrintCardDownloadError] = useState('');

	const [success, setSuccess] = useState(null);
	const [error, setError] = useState(null);
	const { t } = useTranslation('download');
	const { t: tCommon } = useTranslation('common');
	const searchApi = useSearchApi();
	const productsApi = useProductsApi();
	const { search: executeSearch } = searchApi;
	const { downloadPrintCard } = productsApi;

	const handlePrintCardDownload = useCallback(
		async ({ productId }) => {
			if (!productId) {
				return;
			}

			setPrintCardDownloadError('');

			await downloadFileFromApi({
				format: 'pdf',
				defaultFilenamePrefix: `PrintCard_${productId}`,
				executeApiCall: () => downloadPrintCard(productId),
				onError: () => {
					setPrintCardDownloadError(tCommon('downloadError'));
				},
			});
		},
		[downloadPrintCard, tCommon]
	);

	const productsColumnConfig = useMemo(
		() =>
			makeProductsColumnConfig({
				onPrintCardDownload: handlePrintCardDownload,
				downloadLabel: tCommon('download'),
			}),
		[handlePrintCardDownload, tCommon]
	);

	const sort = useCallback(() => {
		if (sorts.length) {
			return sorts
				.map(
					({ key, direction }) =>
						`${SORTKEYS[key] || key} ${direction.toUpperCase()}`
				)
				.join(', ');
		} else if (
			(ordersFilters.status === undefined && !sorts.length) ||
			(Object.keys(ordersFilters.status).every(
				(k) => !ordersFilters.status[k]
			) &&
				!sorts.length &&
				ordersFilters.status !== undefined)
		) {
			return 'Order_Date DESC';
		}
		// this is the default, we will add or change sort fields later
		return 'Order_Date DESC';
	}, [sorts, ordersFilters]);

	const fetchSearchResults = useCallback(async () => {
		setIsLoading(true);
		// this api is set up so that it requires 2 requests to get orders and products search data.
		// the goal is to fire both requests in sequence, prioritizing the one that will return results for the current page
		const searchParams = {
			searchTerm: searchKey,
			limit,
			page,
			filters: { ...ordersFilters, ...productsFilters },
			Order_By: sort(),
		};
		try {
			const isOrdersPage = !/\/products(\/|$)/.test(pathname);

			const response = await executeSearch({
				...searchParams,
				searchType: isOrdersPage ? 'orders' : 'products',
			});

			if (response?.isAborted) {
				setIsLoading(false);
				return;
			}

			if (response?.isError) {
				console.error(response.error || 'failed to load search results');
				setIsLoading(false);
				return;
			}

			const parsedResponse = parseApiResponseData(response, {});
			const { Count_Orders, Count_Products, Orders, Products } = parsedResponse;
			setOrdersCount(Count_Orders || 0);
			setProductsCount(Count_Products || 0);
			setOrders(isOrdersPage ? Orders || [] : []);
			setProducts(isOrdersPage ? [] : Products || []);
		} catch (e) {
			console.error(e);
		}
		setIsLoading(false);
	}, [
		executeSearch,
		searchKey,
		ordersFilters,
		productsFilters,
		page,
		pathname,
		limit,
		sort,
	]);

	useEffect(() => {
		fetchSearchResults();

		const handleStorageChanges = (e) => {
			if (!e.key || e.key === 'success') {
				setSuccess(localStorage.getItem('success'));
			}

			if (!e.key || e.key === 'error') {
				setError(localStorage.getItem('error'));
			}
		};

		window.addEventListener('storage', handleStorageChanges);

		return () => {
			window.removeEventListener('storage', handleStorageChanges);
		};
	}, [fetchSearchResults, ordersFilters]);

	useEffect(() => {
		setPage(0);
	}, [searchKey]);

	return (
		<div {...props}>
			<SideBarMenu ordersCount={ordersCount} productsCount={productsCount} />
			{path === 'orders' ? (
				<OrdersTable
					orders={orders}
					total={ordersCount}
					sorts={sorts}
					onSort={setSorts}
					onPageChange={setPageParams}
					pagination={{ limit, page }}
					columnOrder={columnOrder}
					columnVisibility={columnVisibility}
					columnConfig={ordersColumnConfig}
					isLoading={isLoading}
					filters={ordersFilters}
					onFilter={setOrdersFilters}
					onColumnOrderChange={setColumnOrder}
					onColumnVisibilityChange={setColumnVisibility}
				>
					<Box
						inputpros={{
							'aria-label': 'Error and Success messages',
							role: 'row',
						}}
						component="div"
						sx={{
							display: { xs: 'none', sm: 'block' },
							width: '100%',
							marginLeft: '-.65rem',
						}}
					>
						{error && !success && <Error message={t('error')} />}
						{success && <Success message={t('success')} />}
					</Box>
					{!!sorts.length > 0 && (
						<Box
							inputpros={{
								'aria-label': 'order filters',
								role: 'row',
							}}
							component="div"
							sx={{
								display: 'flex',
								alignItems: 'right',
								justifyContent: 'flex-end',
								margin: {
									xs: '.5rem .2rem auto -.2rem',
									sm: '.1rem .65rem .5rem .5rem',
								},
							}}
						>
							<ClearButton
								onChange={setSorts}
								label="clear_sort"
								icon={
									<ArrowClockwise
										style={{
											fontSize: theme.palette.textSize.sizeXs,
											color: theme.palette.link.main,
										}}
									/>
								}
							/>
						</Box>
					)}

					<Box
						inputpros={{
							'aria-label': 'order filters',
							role: 'row',
						}}
						component="div"
						sx={{
							display: 'inline',
							width: '6rem',
							margin: {
								xs: '.5rem .2rem auto -.2rem',
								sm: '.1rem .65rem .5rem .5rem',
							},
						}}
					>
						<OrderFilters
							onFilter={(...args) => {
								setOrdersFilters(...args);
								setPageParams({ page: 0, limit });
							}}
							value={ordersFilters}
							openRef={(ref) => {
								openFiltersRef.current = ref;
							}}
						/>
					</Box>
					<Box
						inputpros={{
							'aria-label': 'column customizer',
							role: 'row',
						}}
						component="div"
						sx={{
							display: 'inline',
							width: '9.25rem',
							margin: {
								xs: '.5rem .2rem auto -.2rem',
								sm: '.1rem .65rem .5rem .5rem',
							},
						}}
					>
						<ColumnCustomizer
							order={columnOrder}
							visibility={columnVisibility}
							onOrderChange={setColumnOrder}
							onVisibilityChange={setColumnVisibility}
							onReset={resetColumns}
							columnConfig={ordersColumnConfig}
						/>
					</Box>

					<Box
						inputpros={{
							'aria-label': 'data export',
							role: 'row',
						}}
						component="div"
						sx={{
							display: 'inline',
							ml: '1.5rem',
							width: '9.25rem',
							minWidth: {
								xs: '90px',
								sm: '115px',
							},
						}}
					>
						<DataExport
							isLoading={isLoading}
							sort={sort()}
							filters={{ ...ordersFilters }}
							columnConfig={ordersColumnConfig}
							columnOrder={columnOrder}
							columnVisibility={columnVisibility}
							dataTestId="orders_table_export"
							onVisibilityChange={setColumnVisibility}
							onReset={resetColumns}
						/>
					</Box>
				</OrdersTable>
			) : (
				<ProductsTable
					products={products}
					total={productsCount}
					onFilter={setProductsFilters}
					pagination={{ limit, page }}
					onPageChange={setPageParams}
					columnConfig={productsColumnConfig}
					columnVisibility={columnVisibility}
					isLoading={isLoading}
				>
					<Box
						inputpros={{
							'aria-label': 'Error and Success messages',
							role: 'row',
						}}
						component="div"
						sx={{
							display: { xs: 'none', sm: 'block' },
							width: '100%',
							marginLeft: '-.65rem',
						}}
					>
						{!!printCardDownloadError && (
							<Error message={printCardDownloadError} fullWidth />
						)}
					</Box>
					{!!sorts.length > 0 && (
						<Box
							inputpros={{
								'aria-label': 'order filters',
								role: 'row',
							}}
							component="div"
							sx={{
								display: 'flex',
								alignItems: 'right',
								justifyContent: 'flex-end',
								margin: {
									xs: '.5rem .2rem auto -.2rem',
									sm: '.1rem .65rem .5rem .5rem',
								},
							}}
						>
							<ClearButton
								onChange={setSorts}
								label="clear_sort"
								icon={
									<ArrowClockwise
										style={{
											fontSize: theme.palette.textSize.sizeXs,
											color: theme.palette.link.main,
										}}
									/>
								}
							/>
						</Box>
					)}
					<Box
						inputpros={{
							'aria-label': 'order filters',
							role: 'row',
						}}
						component="div"
						sx={{
							display: 'inline',
							width: '6rem',
							margin: {
								xs: '.5rem .2rem auto -.2rem',
								sm: '.1rem .65rem .5rem .5rem',
							},
						}}
					>
						<ProductsFilters
							onFilter={(...args) => {
								setProductsFilters(...args);
								setPageParams({ page: 0, limit });
							}}
							value={productsFilters}
						/>
					</Box>
				</ProductsTable>
			)}
		</div>
	);
}

export default Search;
